import React from "react";
import { Box, Button, IconButton, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { VoicesTopicSource } from "../../../Models/voicesTopics";
import VoicesInput from "../Common/VoicesInput";

interface Props {
  sources: VoicesTopicSource[];
  onChange: (sources: VoicesTopicSource[]) => void;
}

const EMPTY_SOURCE: VoicesTopicSource = { url: "", name: "", date: null };

const VoicesSourcesEditor: React.FC<Props> = ({ sources, onChange }) => {
  const { t } = useTranslation("voicesTopics");
  const { colors: workspace } = useWorkspaceColors();

  const update = (index: number, change: Partial<VoicesTopicSource>) =>
    onChange(
      sources.map((source, position) =>
        position === index ? { ...source, ...change } : source,
      ),
    );

  return (
    <Stack spacing={1.5}>
      {sources.map((source, index) => (
        <Box
          key={index}
          sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}
        >
          <VoicesInput
            placeholder="https://"
            value={source.url}
            onChange={(e) => update(index, { url: e.target.value })}
            sx={{ flex: "2 1 240px" }}
          />
          <VoicesInput
            placeholder={t("sourceName")}
            value={source.name}
            onChange={(e) => update(index, { name: e.target.value })}
            sx={{ flex: "1 1 160px" }}
          />
          <VoicesInput
            type="date"
            value={source.date ?? ""}
            onChange={(e) => update(index, { date: e.target.value || null })}
            sx={{ flex: "0 1 170px" }}
          />
          <IconButton
            size="small"
            disabled={sources.length === 1}
            onClick={() => onChange(sources.filter((_, position) => position !== index))}
            sx={{ mt: 0.5, color: color.gray500 }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() => onChange([...sources, EMPTY_SOURCE])}
          sx={{ color: workspace.accent, textTransform: "none" }}
        >
          {t("addSource")}
        </Button>
      </Box>
    </Stack>
  );
};

export default VoicesSourcesEditor;
